import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import API, { API_VERSION, REQUEST_METHOD } from '../../../api'


export const initialState = {
  submissionFiles: {},
}

// Accepts either an orderId string (order-level fetch) or { orderId, submissionVersion }
// for a fetch scoped to a single submission. The order-level list is capped
// server-side (~100 most-recent versions), so heavily re-run orders drop older
// submissions' files; a scoped fetch always returns the requested submission's files.
const normalizeArgs = (arg) => (arg && typeof arg === 'object' ? arg : { orderId: arg })

export const fetchSubmissionFiles = createAsyncThunk('submissionFiles/fetchSubmissionFiles', async (arg, { rejectWithValue }) => {
  try {
    const { orderId, submissionVersion } = normalizeArgs(arg)
    const queryParams = { order_id: orderId }
    if (submissionVersion != null) {
      queryParams.submission_version = submissionVersion
    }
    const submissionFiles = await API('submission-files/v1',
      API_VERSION.SERVICE).request(REQUEST_METHOD.GET,
      queryParams,
      {}
    )
    return submissionFiles
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

const submissionFilesSlice = createSlice({
  name: 'submissionFiles',
  initialState,
  reducers: {
    // The slice is shared across orders and merges files by _id (never
    // replaces), so it must be cleared when the open order changes — otherwise
    // a prior order's files linger and, since consumers match on
    // submission_version (which repeats per order), surface as the wrong
    // order's document.
    resetSubmissionFiles: () => initialState,
  },
  extraReducers: {
    [fetchSubmissionFiles.pending]: (state, action) => {
      const { submissionVersion } = normalizeArgs(action.meta.arg)
      if (submissionVersion == null) { return }
      const existing = state.submissionFiles || {}
      state.submissionFiles = {
        ...existing,
        byVersionStatus: { ...(existing.byVersionStatus || {}), [submissionVersion]: 'loading' },
      }
    },
    [fetchSubmissionFiles.fulfilled]: (state, action) => {
      const { submissionVersion } = normalizeArgs(action.meta.arg)
      const existing = state.submissionFiles || {}
      // Merge by _id so a scoped (single-version) fetch adds that submission's
      // files without dropping the order-level set, and vice versa. The only
      // consumers filter files by submission_version, so extra entries are inert.
      const byId = {}
      for (const file of (existing.results || [])) { byId[file._id] = file }
      for (const file of (action.payload.results || [])) { byId[file._id] = file }
      state.submissionFiles = {
        ...existing,
        results: Object.values(byId),
        count: submissionVersion == null ? action.payload.count : existing.count,
        byVersionStatus: submissionVersion == null
          ? (existing.byVersionStatus || {})
          : { ...(existing.byVersionStatus || {}), [submissionVersion]: 'loaded' },
      }
    },
    [fetchSubmissionFiles.rejected]: (state, action) => {
      const { submissionVersion } = normalizeArgs(action.meta.arg)
      if (submissionVersion == null) { return }
      const existing = state.submissionFiles || {}
      state.submissionFiles = {
        ...existing,
        byVersionStatus: { ...(existing.byVersionStatus || {}), [submissionVersion]: 'failed' },
      }
    },
  },
})


export const { resetSubmissionFiles } = submissionFilesSlice.actions

export default submissionFilesSlice.reducer
